import SegmentedPicker from "@/Components/UI/Forms/SegmentedPicker";
import { Client, Coach, PageProps } from "@/types/page";
import { Appointment } from "@/types/scheduling/appointment";
import { coachWithCerts } from "@/utils/fullUserNames";
import { usePage } from "@inertiajs/react";
import classNames from "classnames";
import { addWeeks, eachDayOfInterval, min } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { useEffect, useMemo, useState } from "react";
import { LuGlobe } from "react-icons/lu";
import BlockedTimes from "./BlockedTimes";
import CalendarView from "./CalendarView";
import Confirmation from "./Confirmation";
import TimePicker from "./TimePicker";
import {
    addMonthsInTimezone,
    AVAILABILITY_OPTIONS,
    AvailabilityOption,
    calculateAvailableTimes,
    calculateBlockedTimesByDay,
    DateTimePickerSchedulerData,
    dayKey,
    dayOfWeekInTimezone,
    endOfMonthInTimezone,
    getConflictingBlockers,
    MAX_WEEKS_AHEAD_TO_SCHEDULE,
    nextSaturdayInTimezone,
    prettyTimezoneDifference,
    previousSundayInTimezone,
    startOfMonthInTimezone,
} from "./dateTimePickerUtils";

export type { DateTimePickerSchedulerData } from "./dateTimePickerUtils";

export default function DateTimePicker({
    schedulerData,
    onAppointmentSelection,
    recurringIntervalCount,
    reschedulingAppointment,
    coach,
    client,
    initialSelectedTime,
    viewOnly = false,
    hideHeading = false,
}: {
    schedulerData: DateTimePickerSchedulerData;
    onAppointmentSelection: ({
        start,
        end,
        isRecurring,
        note,
    }: {
        start: string;
        end: string;
        isRecurring: boolean;
        note?: string;
    }) => void;
    recurringIntervalCount: number | null;
    reschedulingAppointment?: Appointment;
    coach: Coach;
    client?: Client;
    initialSelectedTime?: Date;
    viewOnly?: boolean;
    hideHeading?: boolean;
}) {
    const { user } = usePage<PageProps>().props;
    const userTimezone = user?.timezone ?? "UTC";

    const isCoachBookingView = user?.type === "coach" && !viewOnly && !!client;

    const userCanToggleRecurring =
        !reschedulingAppointment &&
        recurringIntervalCount !== null &&
        schedulerData.shouldDoRecurringSchedulingFlow;

    const [month, setMonth] = useState(() =>
        startOfMonthInTimezone(new Date(), userTimezone),
    );
    const [day, setDay] = useState<Date | null>(null);
    const [selectedTime, setSelectedTime] = useState<Date | null>(
        initialSelectedTime ?? null,
    );
    const [isRecurring, setIsRecurring] = useState(userCanToggleRecurring);
    const [availabilityOption, setAvailabilityOption] =
        useState<AvailabilityOption>(AVAILABILITY_OPTIONS[0]);

    const monthStart = startOfMonthInTimezone(month, userTimezone);
    const monthEnd = endOfMonthInTimezone(month, userTimezone);
    const calendarStart =
        dayOfWeekInTimezone(monthStart, userTimezone) === 0
            ? monthStart
            : previousSundayInTimezone(monthStart, userTimezone);
    const calendarEnd =
        dayOfWeekInTimezone(monthEnd, userTimezone) === 6
            ? monthEnd
            : nextSaturdayInTimezone(monthEnd, userTimezone);

    const availableTimesByDay = useMemo(
        () =>
            calculateAvailableTimes({
                schedulerData,
                start: calendarStart,
                end: min([
                    calendarEnd,
                    addWeeks(new Date(), MAX_WEEKS_AHEAD_TO_SCHEDULE),
                ]),
                recurringIntervalCount,
                reschedulingAppointment,
                isRecurring,
                userTimezone,
                availabilityOption,
            }),
        [
            schedulerData,
            calendarStart.toISOString(),
            recurringIntervalCount,
            reschedulingAppointment,
            isRecurring,
            userTimezone,
            availabilityOption,
        ],
    );

    const blockedTimesByDay = useMemo(
        () =>
            isCoachBookingView
                ? calculateBlockedTimesByDay({
                      schedulerData,
                      start: calendarStart,
                      end: min([
                          calendarEnd,
                          addWeeks(new Date(), MAX_WEEKS_AHEAD_TO_SCHEDULE),
                      ]),
                      userTimezone,
                  })
                : null,
        [schedulerData, calendarStart.toISOString(), userTimezone],
    );

    const useWiderShiftThreshold = blockedTimesByDay !== null && !!day;

    const blockersForDay = (forDay: Date) =>
        blockedTimesByDay?.[dayKey(forDay, userTimezone)] ?? [];

    const selectedTimeConflicts = selectedTime
        ? getConflictingBlockers(selectedTime, blockersForDay(selectedTime))
        : [];

    useEffect(() => {
        const daysInWeek = eachDayOfInterval({
            start: new Date(),
            end: addWeeks(new Date(), 1),
        });
        const hasAvailableTimes = daysInWeek.some(
            (day) => availableTimesByDay[dayKey(day, userTimezone)],
        );
        if (!hasAvailableTimes) {
            setIsRecurring(false);
        }
    }, []);

    useEffect(() => {
        if (day) {
            document.getElementById("time-picker-container")?.scrollIntoView({
                behavior: "smooth",
            });
        }
    }, [day]);

    return (
        <div className="flex flex-col gap-2">
            {selectedTime && !viewOnly ? (
                <Confirmation
                    selectedTime={selectedTime}
                    isRecurring={isRecurring}
                    recurringIntervalCount={recurringIntervalCount}
                    reschedulingAppointment={reschedulingAppointment}
                    conflictingBlockers={selectedTimeConflicts}
                    onConfirm={(slot) =>
                        onAppointmentSelection({
                            ...slot,
                            isRecurring: isRecurring,
                        })
                    }
                    onBack={() => setSelectedTime(null)}
                />
            ) : (
                <div
                    className={classNames(
                        `@container/picker flex flex-col gap-3 max-w-full w-165`,
                        useWiderShiftThreshold
                            ? "min-[1095px]:w-[calc(min(90dvw,1300px))]"
                            : "md:w-200",
                    )}
                >
                    <div className="grid grid-cols-1 @min-[550px]/picker:grid-cols-2 justify-between gap-2">
                        <div className="@min-[550px]/picker:col-span-2 flex flex-col gap-3">
                            {!hideHeading && (
                                <div className="text-lg font-bold">
                                    {reschedulingAppointment ? (
                                        <>
                                            Reschedule your{" "}
                                            {formatInTimeZone(
                                                new Date(
                                                    reschedulingAppointment.start_at,
                                                ),
                                                userTimezone,
                                                "h:mm b zzz 'session on' MMMM d, yyyy",
                                            )}
                                        </>
                                    ) : (
                                        `Schedule with ${isCoachBookingView && client ? client.first_name : coachWithCerts(coach)}`
                                    )}
                                </div>
                            )}
                            {userCanToggleRecurring && (
                                <button
                                    className="flex items-center gap-2 text-base text-brand-black font-medium -mt-2"
                                    onClick={() => setIsRecurring(!isRecurring)}
                                >
                                    <input
                                        type="checkbox"
                                        data-testid="recurring-toggle-checkbox"
                                        className="h-4 w-4 cursor-pointer accent-brand-red rounded-sm text-brand-red focus:ring-transparent disabled:cursor-not-allowed disabled:bg-brand-lightGray"
                                        checked={isRecurring}
                                        readOnly={true}
                                    />
                                    <span className="text-left">
                                        Repeat{" "}
                                        {recurringIntervalCount === 1
                                            ? "every week"
                                            : `every ${recurringIntervalCount} weeks`}
                                    </span>
                                </button>
                            )}
                        </div>
                        {isCoachBookingView && (
                            <div className="flex flex-col gap-1 @min-[550px]/picker:min-w-75 @min-[550px]/picker:max-w-100 order-1 @min-[550px]/picker:order-0">
                                <SegmentedPicker
                                    dataTestId="availability-option-picker"
                                    options={AVAILABILITY_OPTIONS}
                                    selectedOption={availabilityOption}
                                    onOptionChange={setAvailabilityOption}
                                />
                            </div>
                        )}
                        {isCoachBookingView && client && (
                            <div className="flex items-center h-fit mr-0 @min-[550px]/picker:justify-self-end gap-1.5 text-sm text-brand-gray bg-brand-blueDecal/15 border border-brand-blueDecal rounded-lg px-2 py-[3px]">
                                <LuGlobe className="text-brand-blueDecal" />
                                Client's timezone is{" "}
                                {formatInTimeZone(
                                    new Date(),
                                    client.timezone,
                                    "zzz",
                                )}{" "}
                                {prettyTimezoneDifference(
                                    client.timezone,
                                    userTimezone,
                                )}{" "}
                            </div>
                        )}
                    </div>
                    <div
                        className={classNames(
                            "flex flex-col border-brand-border border rounded-lg",
                            useWiderShiftThreshold
                                ? "min-[1095px]:flex-row"
                                : "md:flex-row",
                        )}
                    >
                        <div className="flex flex-1 p-4 pr-6 justify-center">
                            <CalendarView
                                month={month}
                                viewOnly={viewOnly}
                                availableTimesByDay={availableTimesByDay}
                                onMonthChange={(direction) => {
                                    setDay(null);
                                    setMonth(
                                        addMonthsInTimezone(
                                            month,
                                            direction === "prev" ? -1 : 1,
                                            userTimezone,
                                        ),
                                    );
                                }}
                                selectedDay={day}
                                onDaySelect={setDay}
                            />
                        </div>
                        <div
                            className={classNames(
                                "flex-1 border-brand-border border-t relative",
                                useWiderShiftThreshold
                                    ? "min-[1095px]:border-t-0 min-[1095px]:border-l"
                                    : "md:border-t-0 md:border-l",
                            )}
                        >
                            <div
                                id="time-picker-container"
                                className={classNames(
                                    "inset-0",
                                    useWiderShiftThreshold
                                        ? "min-[1095px]:absolute min-[1095px]:overflow-y-auto"
                                        : "md:absolute md:overflow-y-auto",
                                )}
                            >
                                {day ? (
                                    <div className="h-full p-4">
                                        <TimePicker
                                            day={day}
                                            showRecurringOnly={isRecurring}
                                            availableTimesByDay={
                                                availableTimesByDay
                                            }
                                            dayBlockers={blockersForDay(day)}
                                            clientTimezone={
                                                client?.timezone ?? userTimezone
                                            }
                                            onTimeSelect={setSelectedTime}
                                            viewOnly={viewOnly}
                                        />
                                    </div>
                                ) : (
                                    <div
                                        className={classNames(
                                            "hidden flex-col items-center justify-center h-full",
                                            blockedTimesByDay !== null
                                                ? "md:flex"
                                                : "md:flex",
                                        )}
                                    >
                                        <img
                                            src={`${import.meta.env.VITE_CDN_URL}/images/calendar.webp`}
                                            alt="icon"
                                            className="w-32 ml-1.5 -mr-4.5"
                                        />
                                        <div>Pick a date to begin</div>
                                    </div>
                                )}
                            </div>
                        </div>
                        {blockedTimesByDay !== null && day && (
                            <div
                                className={classNames(
                                    "flex-1 border-brand-border border-t relative",
                                    useWiderShiftThreshold
                                        ? "min-[1095px]:border-t-0 min-[1095px]:border-l"
                                        : "md:border-t-0 md:border-l",
                                )}
                            >
                                <div
                                    className={classNames(
                                        "inset-0",
                                        useWiderShiftThreshold
                                            ? "min-[1095px]:absolute min-[1095px]:overflow-y-auto"
                                            : "md:absolute md:overflow-y-auto",
                                    )}
                                >
                                    <div className="p-4">
                                        <BlockedTimes
                                            blockedTimes={
                                                blockedTimesByDay[
                                                    dayKey(day, userTimezone)
                                                ] ?? []
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="text-sm text-brand-black">
                        Sessions are 50 minutes long.{" "}
                        {user?.type === "client" && (
                            <span>
                                Please message {coachWithCerts(coach)} if you
                                need help scheduling.
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
